import store from "../../../../../../../store";
import {CabinetsSet, CountertopPositions, WallPositions} from "../../../../../helpers/sqftCabinetsDataMapper";
import CabinetLoader from "./modelsLoader/CabinetLoaderV2";
import {CABINET_BATHROOM_LABELS, CABINET_BOXES, CABINET_PART_IDS, SQFT_VIEW_MODE} from "../../../../../constants";
import CountertopLoader from "./modelsLoader/CountertopLoader";
import OverlayNo3dNotifier from "./overlay/OverlayNo3dNotifier";
import {computeSceneMetrics} from "./helpers/sceneMetrics";
import TaskRunner from "./helpers/TaskRunner";
import {addShadowsForAllModels} from "../../../../../helpers/sqft3dArSceneHelper";
import WallsLoader from "./modelsLoader/WallsLoader";
import workspace from "../../../../../components/canvas/core/workspace";
import {reportNonFatalError} from "../../../../../../../misc/errorReporting/errorReporter";

class SceneModelsLoader {
  constructor(sceneViewer, scene, modelScale) {
    this.sceneViewer = sceneViewer;
    this.scene = scene;
    this.modelScale = modelScale;
    this.loader = new window.GLTFLoader();
    this.loadedInstances = [];
    this.modelsCenter = null;
    this.modelsSize = null;
    this.notifier = new OverlayNo3dNotifier(sceneViewer);
    this.modelLoadVersions = {};
    this.failedModelLoads = {};

    this.loaders = {
      cabinets: {
        fetch: async () => CabinetsSet.prepareCabinetsPositions(
          store.getState().sqftState.SQFTViewMode === SQFT_VIEW_MODE.threeDModeFramed
        ),
        loader: new CabinetLoader({ scene, loader: this.loader, modelScale, typeKey: 'cabinets', enhanced3DQuality: sceneViewer.enhanced3DQuality })
      },
      countertops: {
        fetch: CountertopPositions.prepareCountertopPositions,
        loader: new CountertopLoader({ scene, loader: this.loader, modelScale, typeKey: 'countertops', enhanced3DQuality: sceneViewer.enhanced3DQuality })
      },
      walls: {
        fetch: WallPositions.prepareWallPositions,
        loader: new WallsLoader({ scene, loader: this.loader, modelScale, typeKey: 'walls', enhanced3DQuality: sceneViewer.enhanced3DQuality })
      }
    };
  }

  async loadAllModels() {
    const isMobile = store.getState().serverState.isMobile
      || /iPad|iPhone/.test(navigator.userAgent);

    this.loadedInstances = [];

    const tasks = Object.values(this.loaders).map(item => async () => {
      const positions = await item.fetch();
      console.log("positions", positions);
      await this._loadPositions(item.loader, positions);
    });

    if (isMobile) {
      await TaskRunner.sequential(tasks);
    } else {
      await TaskRunner.parallel(tasks);
    }

    //log all material maps QCDES-6966
    const meshTextures = {};
    this.scene.traverse(obj => {
      if (obj.isMesh) {
        const mat = obj.material;
        meshTextures[obj.name || obj.uuid] = {
          map: mat.map ? mat.map.uuid : null,
          normalMap: mat.normalMap ? mat.normalMap.uuid : null,
          roughnessMap: mat.roughnessMap ? mat.roughnessMap.uuid : null,
          metalnessMap: mat.metalnessMap ? mat.metalnessMap.uuid : null,
        };
      }
    });
    //console.log(JSON.stringify(meshTextures, null, 2));

    this._updateMetrics();
    this._checkEmptyScene();
    this.sceneViewer.modelsLoaded = true;
  }

  async reloadModels(typeKey, positions) {
    const entry = this.loaders[typeKey];
    if (!entry) {
      console.log(`Unknown loader type "${typeKey}"`);
      return null
    }
    const { loader } = entry;

    // 1) remove old models
    positions.forEach(data => loader.remove(data));

    // 2) clear loadedInstances
    this.loadedInstances = this.loadedInstances.filter(inst =>
      inst.userData._type !== typeKey ||
      !positions.some(d => loader._makeKey(d) === inst.userData._key)
    );

    // 3) load new
    const fresh = await Promise.all(positions.map(d => loader.load(d)));
    fresh.filter(Boolean).forEach(inst => this.loadedInstances.push(inst));

    // 4) recalculate center
    this._updateMetrics();
    this._refreshCabinetLighting(typeKey);

    // 4) hide no 3d message
    this._checkEmptyScene();

    addShadowsForAllModels(this.scene);

    return fresh;
  }

  /**
   * Rebuilds a model type from current workspace data.
   * `shouldContinue` and the internal load version protect the scene from stale
   * async GLB responses that finish after a newer reload has already started.
   * `clearBeforeLoad` is kept configurable for future model types that can layer
   * fresh instances over existing ones; generated countertops use the default
   * clear-first path so obsolete geometry disappears immediately.
   */
  async reloadAllModelsOfType(typeKey, shouldContinue = () => true, options = {}) {
    const entry = this.loaders[typeKey];
    if (!entry) {
      console.log(`Unknown loader type "${typeKey}"`);
      return null;
    }

    const shouldClearBeforeLoad = options.clearBeforeLoad !== false;
    const shouldSkipEmptySceneCheck = options.skipEmptySceneCheck === true;
    const loadVersion = this._beginModelTypeLoad(typeKey);
    const isCurrentLoad = () => shouldContinue() && this._isCurrentModelTypeLoad(typeKey, loadVersion);
    const positions = await entry.fetch();
    if (!isCurrentLoad()) {
      return null;
    }

    if (shouldClearBeforeLoad) {
      this.removeAllModels(typeKey, { preserveLoadVersion: true, skipEmptySceneCheck: true });
    }

    if (typeKey === 'countertops' && this.hasFailedCabinetLoads()) {
      return null;
    }

    await this._loadPositions(entry.loader, positions, isCurrentLoad);

    if (!isCurrentLoad()) {
      this.removeAllModels(typeKey, { preserveLoadVersion: true, skipEmptySceneCheck: true });
      return null;
    }

    if (typeKey === 'countertops') {
      this.countertopsLoaded = true;
    }

    this._updateMetrics();
    this._refreshCabinetLighting(typeKey);
    if (!shouldSkipEmptySceneCheck) {
      this._checkEmptyScene();
    }

    return positions;
  }

  /**
   * Central countertop refresh entrypoint.
   * Countertops are derived from the cabinet workspace, so callers should not
   * manually remove/load them; this method owns cancellation, cleanup, and the
   * "do not reset camera" behavior expected after edits.
   */
  refreshCountertops(options = {}) {
    const shouldContinue = options.shouldContinue || (() => true);

    return this.reloadAllModelsOfType('countertops', shouldContinue, {
      clearBeforeLoad: options.clearBeforeLoad !== false,
      skipEmptySceneCheck: options.skipEmptySceneCheck === true,
    });
  }

  /**
   * Updates existing model instances from freshly prepared workspace positions.
   * Use this when the underlying GLB geometry is unchanged, for example cabinet
   * drag movement, to avoid async remove/reload races and unnecessary downloads.
   */
  updateModelTransforms(typeKey, positions) {
    const entry = this.loaders[typeKey];
    if (!entry) {
      console.log(`Unknown loader type "${typeKey}"`);
      return;
    }

    positions.forEach((data) => {
      const key = entry.loader._makeKey(data);
      const instance = entry.loader.instances.get(key);
      if (!instance) {
        return;
      }

      const position = entry.loader.getModelPosition(data);
      instance.position.set(position.x, position.y, position.z);
      instance.rotation.y = data.rotation
        ? window.THREE.MathUtils.degToRad(data.rotation)
        : 0;
      instance.userData = { ...instance.userData, ...data };

      if (
        typeKey === 'cabinets'
        && entry.loader.crownMoldingLoader
        && typeof entry.loader.crownMoldingLoader.updatePositionForShape === 'function'
      ) {
        entry.loader.crownMoldingLoader.updatePositionForShape(data.shapeId);
      }
    });

    this._updateMetrics();
    this._refreshCabinetLighting(typeKey);
  }

  /**
   * Returns the active scene instance for a prepared model payload without
   * exposing loader internals to interaction controllers.
   */
  getModelInstance(typeKey, data) {
    const entry = this.loaders[typeKey];
    if (!entry) {
      return null;
    }

    return entry.loader.instances.get(entry.loader._makeKey(data)) || null;
  }

  /**
   * Reuses existing model instances when generated geometry can be moved without
   * being rebuilt. Countertops are generated in absolute workspace coordinates,
   * so a pure cabinet move can be represented as an instance offset plus rekey.
   * The method validates all source instances before mutating any loader keys.
   */
  retargetModelInstances(typeKey, transitions) {
    const entry = this.loaders[typeKey];
    if (!entry) {
      console.log(`Unknown loader type "${typeKey}"`);
      return false;
    }

    if (!Array.isArray(transitions) || transitions.length === 0) {
      return false;
    }

    const { loader } = entry;

    for (const transition of transitions) {
      const oldKey = loader._makeKey(transition.from);
      const instance = loader.instances.get(oldKey);

      if (!instance) {
        return false;
      }
    }

    transitions.forEach((transition) => {
      const oldKey = loader._makeKey(transition.from);
      const newKey = loader._makeKey(transition.to);
      const instance = loader.instances.get(oldKey);
      const position = loader.getModelPosition(transition.to);
      const offset = transition.positionOffset || {};

      loader.instances.delete(oldKey);
      instance.position.set(
        instance.position.x + (offset.x || 0),
        position.y,
        instance.position.z + (offset.z || 0)
      );
      instance.rotation.y = transition.to.rotation
        ? window.THREE.MathUtils.degToRad(transition.to.rotation)
        : 0;
      instance.userData = { ...instance.userData, ...transition.to, _type: typeKey, _key: newKey };
      loader.instances.set(newKey, instance);
    });

    this._updateMetrics();
    this._refreshCabinetLighting(typeKey);

    return true;
  }

  /**
   * Removes every active model of a type from both loader registries and the
   * Three.js scene. The options are lifecycle controls for callers that are in
   * the middle of a managed reload:
   * - `preserveLoadVersion` keeps the current reload token valid after cleanup.
   * - `skipEmptySceneCheck` avoids lazy side effects such as initial countertop
   *   loading or camera positioning while a refresh is still in progress.
   */
  removeAllModels(typeKey, options = {}) {
    const entry = this.loaders[typeKey];
    if (!entry) throw new Error(`Unknown loader type "${typeKey}"`);
    const { loader } = entry;
    const shouldPreserveLoadVersion = options.preserveLoadVersion;
    const shouldSkipEmptySceneCheck = options.skipEmptySceneCheck;

    if (!shouldPreserveLoadVersion) {
      this._beginModelTypeLoad(typeKey);
    }
    this._clearModelTypeFailures(typeKey);

    const sceneInstances = [];
    this.scene.traverse((object) => {
      if (object.userData && object.userData._type === typeKey) {
        sceneInstances.push(object);
      }
    });

    const toRemove = [
      ...this.loadedInstances.filter(inst => inst.userData._type === typeKey),
      ...sceneInstances,
    ].filter((inst, index, instances) => instances.indexOf(inst) === index);

    toRemove.forEach(inst => loader.remove(inst.userData));
    sceneInstances.forEach((inst) => {
      if (inst.parent) {
        inst.parent.remove(inst);
      }
    });
    loader.instances.clear();
    this.loadedInstances = this.loadedInstances.filter(inst => inst.userData._type !== typeKey);

    this._updateMetrics();
    this._refreshCabinetLighting(typeKey);
    if (!shouldSkipEmptySceneCheck) {
      this._checkEmptyScene();
    }
  }

  async _loadPositions(loader, positions, shouldContinue = () => true) {
    const tasks = positions.map(data => async () => {
      if (!shouldContinue()) {
        return;
      }

      let inst = null;
      try {
        inst = await loader.load(data, shouldContinue);
      } catch (error) {
        this._handleModelLoadFailure(loader.typeKey, data, error);
        return;
      }

      if (!shouldContinue()) {
        return;
      }

      if (!inst && loader.typeKey === 'cabinets') {
        this._handleModelLoadFailure(loader.typeKey, data, new Error('Cabinet 3D model URL was not found'));
        return;
      }

      this._clearModelLoadFailure(loader.typeKey, data);

      if (inst && shouldContinue()) {
        this.loadedInstances.push(inst);
      }
    });
    const isMobile = store.getState().serverState.isMobile
      || /iPad|iPhone/.test(navigator.userAgent);

    if (isMobile) {
      await TaskRunner.sequential(tasks);
    } else {
      await TaskRunner.parallel(tasks);
    }
  }

  _updateMetrics() {
    const { center, size } = computeSceneMetrics(this.loadedInstances);
    this.modelsCenter = center;
    this.modelsSize   = size;
  }

  _refreshCabinetLighting(typeKey) {
    if (
      typeKey !== 'cabinets'
      || !this.sceneViewer
      || typeof this.sceneViewer.refreshCabinetLighting !== 'function'
    ) {
      return;
    }

    this.sceneViewer.refreshCabinetLighting();
  }

  _checkEmptyScene() {
    if (!this._hasCabinetShapesOnWorkspace()) {
      this.notifier.hide();
      return;
    }

    const count = this.loadedInstances.filter(i => this._getCabinetPartIds().includes(i.userData.partId)).length;
    if (!count) {
      this.notifier.showNo3D();
    } else {
      this.notifier.hide();
      if (!this.countertopsLoaded && !this.hasFailedCabinetLoads()) {
        this._loadCountertopsOnce().then(r => void r);
        this.sceneViewer.calculateAndSetCameraPosition(
          this.sceneViewer.modelsLoader.modelsCenter,
          this.sceneViewer.modelsLoader.modelsSize
        );
      }
    }
  }

  _hasCabinetShapesOnWorkspace() {
    const cabinetPartIds = this._getCabinetPartIds();

    return workspace.getShapes().some(shape => (
      cabinetPartIds.includes(shape.partId)
      || cabinetPartIds.includes(shape.parentPartId)
    ));
  }

  _getCabinetPartIds() {
    return [
      ...CABINET_PART_IDS,
      ...CABINET_BOXES,
      ...Object.keys(CABINET_BATHROOM_LABELS),
    ];
  }

  /**
   * Lazy initial countertop load used only after the cabinet scene first exists.
   * Later countertop edits should use `refreshCountertops` so camera position is
   * preserved and stale initial loads are cancelled.
   */
  async _loadCountertopsOnce() {
    if (this.hasFailedCabinetLoads()) {
      return;
    }

    const loadVersion = this._beginModelTypeLoad('countertops');
    const isCurrentLoad = () => this._isCurrentModelTypeLoad('countertops', loadVersion);
    const entry = this.loaders['countertops'];
    const positions = await entry.fetch();
    await this._loadPositions(entry.loader, positions, isCurrentLoad);
    if (!isCurrentLoad()) {
      return;
    }

    this.countertopsLoaded = true;
    this._updateMetrics();
  }

  // Starts a new logical load cycle for a model type and invalidates older async work.
  _beginModelTypeLoad(typeKey) {
    this.modelLoadVersions[typeKey] = (this.modelLoadVersions[typeKey] || 0) + 1;
    return this.modelLoadVersions[typeKey];
  }

  // Checks whether an async load still belongs to the latest cycle for its type.
  _isCurrentModelTypeLoad(typeKey, loadVersion) {
    return this.modelLoadVersions[typeKey] === loadVersion;
  }

  _getModelFailureKey(typeKey, data) {
    const entry = this.loaders[typeKey];

    return entry ? entry.loader._makeKey(data) : `${typeKey}__${data.partId}__${data.id || data.shapeId}`;
  }

  _getFailureMap(typeKey) {
    if (!this.failedModelLoads[typeKey]) {
      this.failedModelLoads[typeKey] = new Map();
    }

    return this.failedModelLoads[typeKey];
  }

  _clearModelLoadFailure(typeKey, data) {
    const failureMap = this.failedModelLoads[typeKey];

    if (!failureMap) {
      return;
    }

    failureMap.delete(this._getModelFailureKey(typeKey, data));
  }

  _clearModelTypeFailures(typeKey) {
    if (this.failedModelLoads[typeKey]) {
      this.failedModelLoads[typeKey].clear();
    }
  }

  _handleModelLoadFailure(typeKey, data, error) {
    const key = this._getModelFailureKey(typeKey, data);
    const failureMap = this._getFailureMap(typeKey);

    failureMap.set(key, {
      data,
      error,
    });

    if (typeKey !== 'cabinets') {
      return;
    }

    reportNonFatalError('sqft-3d-cabinet-model-load-failed', error, {
      modelData: data,
      modelKey: key,
      modelUrl: data && data.url,
      materialUrl: data && data.material_url,
    });
  }

  hasFailedCabinetLoads() {
    const failureMap = this.failedModelLoads.cabinets;

    return !!(failureMap && failureMap.size);
  }

  hasFailedCabinetLoadForShape(shapeId) {
    const failureMap = this.failedModelLoads.cabinets;

    if (!failureMap || !shapeId) {
      return false;
    }

    return Array.from(failureMap.values()).some(failure => (
      failure.data && failure.data.shapeId === shapeId
    ));
  }
}

export default SceneModelsLoader;
